import { AfterViewInit, Component, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { skip } from 'rxjs';

import { AuthService } from 'src/app/auth/services/auth.service';
import { CLAIM_PAGE } from 'src/app/infrastructure/injection-tokens/PAGE-NAME';
import { FooterService } from 'src/app/features/shared/footer/footer.service';
import { HeaderService } from 'src/app/features/shared/header/services/header.service';
import { IFooterData } from 'src/app/features/shared/footer/models/footer-data';
import { IHeaderData } from 'src/app/features/shared/header/models/header-data';
import { InputControlComponent } from 'src/app/features/shared/controls/container/input-control/input-control.component';
import { LoadingPageService } from 'src/app/infrastructure/services/loading-services/loading-page.service';
import { NotificationService } from 'src/app/features/shared/notification/notification.service';
import { SharedModule } from 'src/app/features/shared/shared.module';
import { _CentralPageService } from 'src/app/features/shared/central-page/services/_central-page.service';
import { _PageService } from 'src/app/infrastructure/services/page-services/_page.service';
import { _SectionBaseComponent } from 'src/app/infrastructure/components/section-components/_section-base-component';
import { injectRequired } from 'src/app/infrastructure/functions/inject-required.function';
import { passwordMatchValidator, passwordValidators } from '../../../functions/password.validators';
import { sectionLayout, TSectionLayout } from 'src/app/infrastructure/models/section-layout';

// Edit-sectie van PasswordChangeComponent (pagina start in edit-modus): de standaard save-knop in de
// footer (button-block-edit-section) stuurt dit formulier aan via _CentralPageService. Geen
// _SectionEditComponent: dat is gebouwd rond een entiteit (form-model-/dto-service, create/patch),
// hier is het één losse actie zonder record.
@Component({
    selector: 'app-password-change-edit',
    templateUrl: './password-change-edit.component.html',
    standalone: true,
    imports: [
        SharedModule,
        InputControlComponent,
    ],
    styleUrls: [
        '../../../account.scss',
        './password-change-edit.component.scss',
    ]
})
export class PasswordChangeEditComponent
    extends _SectionBaseComponent
    implements OnInit, AfterViewInit
{
    private readonly __authService = injectRequired(AuthService);
    private readonly __claimPage = inject(CLAIM_PAGE, { optional: true });
    private readonly __formBuilder = injectRequired(UntypedFormBuilder);
    private readonly __notificationService = injectRequired(NotificationService);

    protected override readonly _centralPageService = injectRequired(_CentralPageService);
    protected override readonly _footerService = injectRequired(FooterService);
    protected override readonly _headerService = injectRequired(HeaderService);
    protected override readonly _loadingPageService = injectRequired(LoadingPageService);
    protected override readonly _pageService = injectRequired(_PageService);

    override out_onEscape = this._out_onEscape;
    override out_onPageLoaded = this._out_onPageLoaded;

    override readonly sectionLayout$$ = signal<TSectionLayout>(sectionLayout.form);

    override template_footerLeft$$ = this._template_footerLeft$$;
    override template_sectionDetails_extraButtons$$ = this._template_sectionDetails_extraButtons$$;
    override template_sectionEdit_extraButtons$$ = this._template_sectionEdit_extraButtons$$;

    override footerData: Partial<IFooterData> = {
        hideSideBlockLeft: false,
        hideSideBlockRight: false,
    };
    override headerData: Partial<IHeaderData> = {
        title: 'wachtwoord wijzigen',
    };

    private __form: UntypedFormGroup = this.__formBuilder.group({
        currentPassword: ['', Validators.required],
        newPassword: ['', passwordValidators],
        newPasswordRepeat: ['', Validators.required],
    }, { validators: passwordMatchValidator('newPassword', 'newPasswordRepeat') });

    get Form() { return this.__form; }
    // Cast naar FormControl: UntypedFormGroup.controls levert AbstractControl, maar
    // app-input-control verwacht een FormControl-input (runtime zijn het FormControls).
    get FormControls() { return this.__form.controls as { [key: string]: FormControl }; }

    showErrors = false;

    constructor() {
        super();

        // Save-knop pas actief als aan alle validatieregels is voldaan (verplicht, wachtwoordbeleid,
        // herhaling gelijk) — zelfde regel als FormComponent (disabled zolang form.invalid).
        this.__form.statusChanges
            .pipe(takeUntilDestroyed())
            .subscribe(() => this._centralPageService.btnSaveData_set({ disabled: this.__form.invalid }));

        // Footer-knoppen. skip(1): de streams zijn BehaviorSubjects en spelen bij subscribe de laatste
        // waarde opnieuw af (zelfde reden als het __initialize-filter in _SectionEditComponent).
        this._centralPageService.onSaveEmit$
            .pipe(skip(1), takeUntilDestroyed())
            .subscribe(() => this.emit_submit());
        this._centralPageService.onCancelEmit$
            .pipe(skip(1), takeUntilDestroyed())
            .subscribe(() => this.emit_onEscape());
    }

    override ngOnInit(): void {
        super._ngOnInit();
        // Knop-zichtbaarheid via de claims van deze pagina (BtnPageSave_Show, BtnPageCancel_Show —
        // die laatste bewust niet geseed: de footer heeft al een terug-knop). Save disabled tot het
        // formulier geldig is.
        if (this.__claimPage) this._centralPageService.pageName_sectionEdit_set(this.__claimPage);
        this._centralPageService.btnSaveData_set({ disabled: true });

        // De claims van deze pagina zijn al binnen via de route-resolver (Page_Read-poort).
        this.emit_pageLoaded();
    }

    override emit_onEscape(): void {
        super._emit_onEscape();
    }

    override emit_pageLoaded(): void {
        super._emit_pageLoaded();
    }

    emit_submit(): void {
        // Enter in het formulier volgt de save-knop: zonder knop (geen BtnPageSave_Show) geen opslaan.
        if (this._centralPageService.btnSaveData$$().hide) return;
        if (this.__form.invalid) {
            this.showErrors = true;
            return;
        }

        const { currentPassword, newPassword } = this.__form.value;

        this.__authService.postChangePassword$(currentPassword, newPassword)
            .subscribe({
                next: () => {
                    this.__notificationService.pushNotification({
                        severity: 'success',
                        title: 'Wachtwoord gewijzigd',
                        text: 'Je nieuwe wachtwoord is direct actief.',
                    });
                    this.emit_onEscape();
                },
                // Verkeerd huidig wachtwoord / beleidsfout: centraal gerapporteerd, form blijft staan.
                error: () => { /* afgehandeld door ErrorReportingService */ },
            });
    }

    override ngAfterViewInit(): void {
        super._ngAfterViewInit();
    }
}
